/**
 * Utility functions for password generation and management
 */

/**
 * Generates a random password with the specified length and complexity
 * @param {number} length - Length of the password (default: 12)
 * @param {boolean} includeUppercase - Include uppercase letters (default: true)
 * @param {boolean} includeLowercase - Include lowercase letters (default: true)
 * @param {boolean} includeNumbers - Include numbers (default: true)
 * @param {boolean} includeSpecialChars - Include special characters (default: true)
 * @returns {string} - Generated password
 */
export const generateRandomPassword = (
  length = 12,
  includeUppercase = true,
  includeLowercase = true,
  includeNumbers = true,
  includeSpecialChars = true
) => {
  // Define character sets
  const uppercaseChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lowercaseChars = 'abcdefghijklmnopqrstuvwxyz';
  const numberChars = '0123456789';
  const specialChars = '!@#$%^&*()-_=+[]{}|;:,.<>?';
  
  // Combine character sets based on parameters
  let allChars = '';
  if (includeUppercase) allChars += uppercaseChars;
  if (includeLowercase) allChars += lowercaseChars;
  if (includeNumbers) allChars += numberChars;
  if (includeSpecialChars) allChars += specialChars;
  
  // Ensure at least one character set is selected
  if (allChars.length === 0) {
    allChars = lowercaseChars + numberChars;
  }
  
  // Generate password
  let password = '';
  
  // Ensure the password contains at least one character from each requested set
  if (includeUppercase) {
    password += uppercaseChars.charAt(Math.floor(Math.random() * uppercaseChars.length));
  }
  if (includeLowercase) {
    password += lowercaseChars.charAt(Math.floor(Math.random() * lowercaseChars.length));
  }
  if (includeNumbers) {
    password += numberChars.charAt(Math.floor(Math.random() * numberChars.length));
  }
  if (includeSpecialChars) {
    password += specialChars.charAt(Math.floor(Math.random() * specialChars.length));
  }
  
  // Fill the remaining length with random characters
  for (let i = password.length; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * allChars.length);
    password += allChars.charAt(randomIndex);
  }
  
  // Shuffle the password characters (Fisher-Yates algorithm)
  const passwordArray = password.split('');
  for (let i = passwordArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [passwordArray[i], passwordArray[j]] = [passwordArray[j], passwordArray[i]];
  }
  
  return passwordArray.join('');
}; 